import React from 'react';
import { User, Bot, CheckCircle2, FileText, Settings, Sparkles } from 'lucide-react';
import { ChatMessage as ChatMessageType } from '@/hooks/useGuideAIChat';
import ChatAttachment from './ChatAttachment';
import { motion } from 'framer-motion';
import { Card, CardBody, Chip, Avatar } from '@heroui/react';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';

  const getUpdateTypeIcon = (type: string) => {
    switch (type) {
      case 'guide':
        return <FileText className="w-3 h-3" />;
      case 'step':
        return <Sparkles className="w-3 h-3" />;
      case 'metadata':
        return <Settings className="w-3 h-3" />;
      default:
        return <CheckCircle2 className="w-3 h-3" />;
    }
  };

  const getUpdateTypeLabel = (type: string) => {
    switch (type) {
      case 'guide':
        return 'Guide';
      case 'step':
        return 'Step';
      case 'metadata':
        return 'Metadata';
      default:
        return 'Update';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <Avatar
          icon={<Bot className="w-4 h-4" />}
          classNames={{
            base: 'bg-primary-500 flex-shrink-0',
            icon: 'text-white',
          }}
          size="sm"
        />
      )}

      <div className={`flex flex-col gap-2 max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}>
        <Card
          className={`${
            isUser
              ? 'max-w-full w-full bg-primary-500 text-white'
              : 'max-w-full w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-gray-700'
          } shadow-sm`}
        >
          <CardBody className="px-4 py-3">
            {isAssistant && message.content && (
              <div
                className="whitespace-pre-wrap break-words leading-relaxed text-sm"
                dangerouslySetInnerHTML={{ __html: message.content?.trim() }}
              />
            )}
            {isUser && <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>}
            {message.isStreaming && (
              <span className="inline-block w-2 h-2 bg-current rounded-full animate-pulse ml-1" />
            )}
          </CardBody>
        </Card>

        {message.fileAttachments && message.fileAttachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.fileAttachments.map((attachment) => (
              <ChatAttachment key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        {isAssistant && message.guideUpdates && (
          <Card className="w-full border-2 border-success-500 dark:border-success-400 bg-success-50 dark:bg-success-900/20 shadow-sm">
            <CardBody className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-success-600 dark:text-success-400" />
                <span className="text-sm font-semibold text-success-700 dark:text-success-300">Guide Updated</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Chip
                  size="sm"
                  variant="flat"
                  color="success"
                  startContent={getUpdateTypeIcon(message.guideUpdates.type)}
                >
                  {getUpdateTypeLabel(message.guideUpdates.type)}
                </Chip>
                {message.guideUpdates.changes && Object.keys(message.guideUpdates.changes).length > 0 && (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {Object.keys(message.guideUpdates.changes).length} field(s) updated
                  </span>
                )}
              </div>
              {message.guideUpdates.changes && Object.keys(message.guideUpdates.changes).length > 0 && (
                <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  <div className="font-medium mb-1">Updated fields:</div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {Object.keys(message.guideUpdates.changes)
                      .slice(0, 5)
                      .map((key) => (
                        <li key={key}>{key}</li>
                      ))}
                    {Object.keys(message.guideUpdates.changes).length > 5 && (
                      <li>+{Object.keys(message.guideUpdates.changes).length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </CardBody>
          </Card>
        )}

        <span className="text-xs text-gray-400 dark:text-gray-500 px-1">
          {new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>

      {isUser && (
        <Avatar
          icon={<User className="w-4 h-4" />}
          classNames={{
            base: 'bg-gray-300 dark:bg-gray-700 flex-shrink-0',
            icon: 'text-gray-600 dark:text-gray-300',
          }}
          size="sm"
        />
      )}
    </motion.div>
  );
};

export default ChatMessage;
